<template>
  <div class="dayselectdropdown" v-on-clickaway="closeDropdown">
    <slot name="trigger" :toggleDropdown="toggleDropdown" :selectedDate="selectedDate">
    </slot>
    <div :class="['dayselectdropdown__dropdown', dropdownOpen && 'dayselectdropdown__dropdown--open']">

      <div class="dayselectdropdown__yearpanel">
        <button @click="addYear(-1)">
          <icon icon="chevron-left"></icon>
        </button>
        <span>{{ displayYear }}</span>
        <button @click="addYear(1)">
          <icon icon="chevron-right"></icon>
        </button>
      </div>

      <div class="dayselectdropdown__monthpanel">
        <button @click="addMonth(-1)">
          <icon icon="chevron-left"></icon>
        </button>
        <span>{{ displayMonth }}</span>
        <button @click="addMonth(1)">
          <icon icon="chevron-right"></icon>
        </button>
      </div>

      <div class="dayselectdropdown__dayspanel">
        <span v-for="(label, index) of daysHeaders" 
              :key="index + 100"
              class="dayselectdropdown_dayheader">
          {{ label }}
        </span>
        <span v-for="(day, index) of days"
              :key="index"
              :class="['dayselectdropdown__day', day.current && 'dayselectdropdown__day--current', day.selected && 'dayselectdropdown__day--selected', day.outsideMonth && 'dayselectdropdown__day--outside']"
              @click="selectDay(day)">
          {{ day.number }}
        </span>
      </div>

    </div>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import moment from 'moment';
import _ from 'lodash'

export default {
  name: 'day-select-dropdown',
  mixins: [ clickaway ],
  props: {
    selectedDate: {
      type: Date,
      required: false
    },
    mode: {
      type: String,
      default: 'week',
      validator: (value) => ['week','day'].indexOf(value) !== -1
    }
  },
  data: () => ({
    dropdownOpen: false,
    date: undefined,
    allowClose: true
  }),
  mounted() {
    this.date = (this.selectedDate) ? this.selectedDate : new Date();
  },
  methods: {
    addYear(amount) {
      this.date = moment(this.date).add(amount, 'years').toDate();
    },
    addMonth(amount) {
      this.date = moment(this.date).add(amount, 'months').toDate();
    },
    closeDropdown() {
      if (this.allowClose) {
        this.dropdownOpen = false;
      }
    },
    delayClose: _.debounce(function() {
      this.allowClose = true;
    }),
    isCurrentDate(momentDate) {
      return moment().isSame(momentDate, 'day');
    },
    isSelectedWeek(momentDate) {
      const selectedMoment = (this.selectedDate) ? moment(this.selectedDate) : moment();
      return momentDate.isSame(selectedMoment, this.mode);
    },
    isOutsideMonth(momentDate) {
      return !moment(this.date).isSame(momentDate, 'month');
    },
    selectDay(day) {
      this.$emit('date-selected', day.date);
      this.dropdownOpen = false;
    },
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
    }
  },
  computed: {
    startDate() {
      return moment(this.date).date(1).isoWeekday(1).toDate();
    },
    endDate() {
      return moment(this.date).add(1, 'month').day(1).add(-1, 'day').isoWeekday(7).toDate();
    },
    days() {
      const monthDays = [];

      for (let day = this.startDate; day <= this.endDate; day = moment(day).add(1, 'day').toDate()) {
        const momentDay = moment(day);
        monthDays.push({
          number: momentDay.date(),
          date: momentDay.toDate(),
          current: this.isCurrentDate(momentDay),
          selected: this.isSelectedWeek(momentDay),
          outsideMonth: this.isOutsideMonth(momentDay)
        });
      }

      return monthDays;
    },
    daysHeaders() {
      const headers = [];
      for (let i = 0; i < 7; i++) {
        headers[i] = moment(this.days[i].date).format('ddd')[0];
      }
      return headers;
    },
    displayYear() {
      return moment(this.date).format('YYYY');
    },
    displayMonth() {
      return moment(this.date).format('MMMM');
    }
  },
  watch: {
    dropdownOpen(newValue) {
      if (newValue) {
        this.allowClose = false;
        this.delayClose();
      }
    }
  }

}
</script>

<style lang="scss">
.dayselectdropdown {
  position: relative;

  .dayselectdropdown__dropdown {
    visibility: hidden;
    position: absolute;
    z-index: 10;
    width: 280px;
    height: auto;
    background-color: transparent;
    top: 31px;
    right: 0px;

    &.dayselectdropdown__dropdown--open {
      visibility: visible;
    }

    .dayselectdropdown__monthpanel,
    .dayselectdropdown__yearpanel {
      display: flex;
      flex-flow: row nowrap;
      justify-content: space-between;
      align-items: center;
      gap: 5px;
      background-color: #c9c8c8;
      padding: 2px 5px 3px 5px;

      button {
        flex: 1 1 auto;
        background-color: #0099d9;
        height: 32px !important;
        border: none;
        cursor: pointer;
        display: flex;
        flex-flow: row nowrap;
        justify-content: center;
        align-items: center;
      }
      span {
        flex: 1 0 40%;
        height: 32px;
        font-size: 21px;
        background-color: #adadad;
        color: white;
        text-transform: capitalize;
      }
    }
    .dayselectdropdown__dayspanel {
      display: flex;
      flex-flow: row wrap;
      border: 2px solid #adadad;
      background-color: white;

      .dayselectdropdown_dayheader,
      .dayselectdropdown__day {
        flex: 0 1 14.286%;
        height: 31px;
        display: flex;
        flex-flow: column;
        justify-content: center;
        background-color: #fff;
      }
      .dayselectdropdown_dayheader {
        border-bottom: 2px solid #c9c8c8;
        text-transform: capitalize;
      }
      .dayselectdropdown__day {
        transition: background-color .15s ease-out 0s;
        &.dayselectdropdown__day--current {
          font-weight: 800;
          border: 1px dashed #c9c8c8;
        }

        &.dayselectdropdown__day--selected {
          background-color: #ebf5ff;
        }

        &.dayselectdropdown__day--outside {
          background-color: #c9c8c8;
          color: #adadad;
        }

        &:hover {
          background-color: #f3f3f3;
          transition: background-color .0s ease-out 0s;
        }
      }
    }
  }
}
</style>
